import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAuthConfig } from "@/lib/useAuthConfig";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { AuthLayout } from "@/components/AuthLayout";
import { Field } from "@/components/ui/Field";
import { Button } from "@/components/ui/Button";
import { ArrowRightIcon } from "@/lib/icons";

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const config = useAuthConfig();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(
    searchParams.get("error") ? mapError(searchParams.get("error")!) : null,
  );
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const auth = await api.login({ email, password });
      signIn(auth);
      navigate("/");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in to TwilioPulse"
      subtitle="Welcome back. Log in to your workspace."
      footer={
        <>
          New to TwilioPulse?{" "}
          <Link
            to="/signup"
            className="font-medium text-brand-600 transition hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
          >
            <span className="inline-flex items-center gap-1">
              Create a workspace
              <ArrowRightIcon size={12} />
            </span>
          </Link>
        </>
      }
    >
      {config?.google.enabled && (
        <>
          <GoogleSignInButton mode="login" />
          <Divider />
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Work email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          required
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        )}
        <Button type="submit" block disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  );
}

function Divider() {
  return (
    <div className="relative my-5 text-center">
      <span className="absolute inset-x-0 top-1/2 h-px bg-zinc-200 dark:bg-zinc-800" />
      <span className="relative bg-white px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:bg-zinc-900/70 dark:text-zinc-500">
        or
      </span>
    </div>
  );
}

function mapError(code: string): string {
  switch (code) {
    case "user_not_found":
      return "No account found for that Google identity. Try signing up.";
    case "google_oauth_disabled":
      return "Google sign-in is not enabled on this server.";
    case "invalid_state":
      return "Sign-in expired. Please try again.";
    default:
      return "Sign-in failed. Please try again.";
  }
}
